import React, { useCallback, useEffect, useRef, useState } from "react";
import SteffiFace from "./SteffiFace";
import { RENDERS } from "../renders";

/* A Steffi render with a living face on it.
 *
 * The photograph and the face share one wrapper, so everything inside stays
 * pixel-locked while the unit floats or scales.
 */
/* How long her eyes stay hearts after the stroking stops. */
const HEARTS_MS = 1600;
/* The cut-out bot leans towards the pointer by up to this many degrees, so it
   reads as an object in space rather than a flat picture. */
const MAX_TILT = 6;

/* Leans the element towards the pointer while it is on screen. */
function useTilt(ref, enabled) {
  useEffect(() => {
    const node = ref.current;
    if (!node || !enabled) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    if (!window.matchMedia("(hover: hover)").matches) return undefined;

    let visible = false;
    let frame = 0;
    const target = { x: 0, y: 0 };
    const current = { x: 0, y: 0 };

    const paint = () => {
      current.x += (target.x - current.x) * 0.08;
      current.y += (target.y - current.y) * 0.08;
      node.style.transform =
        `perspective(1100px) rotateX(${(-current.y * MAX_TILT).toFixed(2)}deg) ` +
        `rotateY(${(current.x * MAX_TILT).toFixed(2)}deg)`;
      const settled = Math.abs(target.x - current.x) < 0.002 && Math.abs(target.y - current.y) < 0.002;
      frame = visible && !settled ? requestAnimationFrame(paint) : 0;
    };
    const onMove = (e) => {
      if (!visible) return;
      const r = node.getBoundingClientRect();
      const reach = Math.max(window.innerWidth, window.innerHeight) * 0.5;
      target.x = Math.max(-1, Math.min(1, (e.clientX - (r.left + r.width / 2)) / reach));
      target.y = Math.max(-1, Math.min(1, (e.clientY - (r.top + r.height / 2)) / reach));
      if (!frame) frame = requestAnimationFrame(paint);
    };
    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
    });
    io.observe(node);
    window.addEventListener("pointermove", onMove, { passive: true });
    return () => {
      io.disconnect();
      window.removeEventListener("pointermove", onMove);
      cancelAnimationFrame(frame);
    };
  }, [ref, enabled]);
}

export default function SteffiDevice({
  variant = "front",
  live = true,
  awake = true,
  speaking = false,
  glanceToken = 0,
  glanceDir = 1,
  followPointer = true,
  float = true,
  /** Hovering the render turns her eyes into hearts. */
  heartsOnHover = true,
  /** Anything to render on the glass itself, e.g. an incoming call. */
  screen = null,
  /** Lean towards the pointer, like a real object on the page. */
  tilt = true,
  priority = false,
  alt = "",
  className = "",
  style,
  unitRef,
  unitStyle,
}) {
  const r = RENDERS[variant];
  const f = r.face;
  const tiltRef = useRef(null);
  useTilt(tiltRef, tilt);

  /* Stroke her display with the mouse and her eyes turn to hearts; they stay
     while the stroking goes on and settle a moment after it stops. Pointer
     only — a touch device has no hover, and it must never latch on. */
  const [hearts, setHearts] = useState(false);
  const heartTimer = useRef(null);

  const swoon = useCallback(
    (event) => {
      if (!heartsOnHover || event.pointerType !== "mouse") return;
      clearTimeout(heartTimer.current);
      setHearts(true);
      heartTimer.current = setTimeout(() => setHearts(false), HEARTS_MS);
    },
    [heartsOnHover]
  );

  useEffect(() => () => clearTimeout(heartTimer.current), []);

  /* The face may only light up once the photograph it belongs to is on screen,
     otherwise it floats on the background while the render decodes. */
  const [loaded, setLoaded] = useState(false);
  const onLoad = useCallback(() => setLoaded(true), []);
  const attachImg = useCallback((node) => {
    // a cached image can already be complete before onLoad would ever fire
    if (node && node.complete && node.naturalWidth > 0) setLoaded(true);
  }, []);

  return (
    <div className={`relative ${className}`} style={style}>
      <div
        ref={unitRef}
        className={float ? "steffi-unit steffi-unit--float" : "steffi-unit"}
        style={{ position: "relative", ...unitStyle }}
      >
        {/* a soft shadow on the floor under the base; it stays put while the
            bot leans, which is what makes the lean read as depth */}
        {r.ground && (
          <span
            aria-hidden
            className="steffi-ground"
            style={{ top: `${r.ground.y}%`, width: `${r.ground.w}%` }}
          />
        )}
        {/* the photo and everything drawn on its glass lean together */}
        <div ref={tiltRef} className="steffi-tilt" style={{ position: "relative" }}>
        <img
          ref={attachImg}
          onLoad={onLoad}
          src={r.src}
          width={r.w}
          height={r.h}
          alt={alt}
          decoding="async"
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : undefined}
          className="product block h-auto w-full"
          draggable="false"
        />

        {screen && loaded && (
          <div
            className="face-layer overflow-hidden"
            style={{
              left: `${f.cx}%`,
              top: `${f.cy}%`,
              width: `${r.outline.rx * 2}%`,
              height: `${r.outline.ry * 2}%`,
              borderRadius: "50%",
              transform: `translate(-50%, -50%) rotate(${r.outline.angle}deg)`,
              // so the on-glass UI can size itself against the display, not the page
              containerType: "inline-size",
            }}
          >
            <div
              className="h-full w-full"
              style={{ transform: `rotate(${-r.outline.angle}deg)`, fontSize: "9cqw" }}
            >
              {screen}
            </div>
          </div>
        )}

        {/* the display is the spot to stroke: it takes the pointer even where
            a parent ignores it (the speak chapter's backdrop) */}
        {live && loaded && heartsOnHover && (
          <div
            aria-hidden
            onPointerEnter={swoon}
            onPointerMove={swoon}
            style={{
              position: "absolute",
              left: `${f.cx}%`,
              top: `${f.cy}%`,
              width: `${r.outline.rx * 2}%`,
              height: `${r.outline.ry * 2}%`,
              borderRadius: "50%",
              transform: `translate(-50%, -50%) rotate(${r.outline.angle}deg)`,
              pointerEvents: "auto",
              zIndex: 2,
            }}
          />
        )}

        {live && loaded && (
          <div
            className="face-layer"
            style={{
              left: `${f.cx}%`,
              top: `${f.cy}%`,
              width: `${f.rw * 2}%`,
              height: `${f.rh * 2}%`,
              transform: `translate(-50%, -50%) ${f.transform}`,
            }}
          >
            <SteffiFace
              awake={awake}
              speaking={speaking}
              hearts={hearts && awake}
              glanceToken={glanceToken}
              glanceDir={glanceDir}
              followPointer={followPointer}
            />
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
